import { useState, useEffect, useRef } from 'react';
import {
    getGlobalModelConfiguration,
    updateGlobalModelConfiguration,
    deleteGlobalModelConfiguration,
    getAvailableModelsForConfiguration
} from '../services/api/globalModelConfigurationApi';

export const useGlobalModelConfiguration = () => {
    const [configuration, setConfiguration] = useState(null);
    const [availableModels, setAvailableModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const hasFetched = useRef(false);

    useEffect(() => {
        if (!hasFetched.current) {
            hasFetched.current = true;
            fetchConfiguration();
            fetchAvailableModels();
        }
    }, []);

    const fetchConfiguration = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getGlobalModelConfiguration();
            setConfiguration(response.data);
        } catch (err) {
            if (err.response?.status === 404) {
                // No configuration exists yet, that's okay
                setConfiguration(null);
            } else {
                setError(err.response?.data?.message || 'Failed to load configuration');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableModels = async () => {
        try {
            const response = await getAvailableModelsForConfiguration();
            setAvailableModels(response.data);
        } catch (err) {
            console.error('Failed to load available models:', err);
        }
    };

    const saveConfiguration = async (data) => {
        setSaving(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const response = await updateGlobalModelConfiguration(data);
            setConfiguration(response.data);
            setSuccessMessage('Configuration saved successfully');
            return response.data;
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.response?.data || 'Failed to save configuration';
            setError(errorMsg);
            throw err;
        } finally {
            setSaving(false);
        }
    };

    const deleteConfiguration = async () => {
        setSaving(true);
        setError(null);
        setSuccessMessage(null);
        try {
            await deleteGlobalModelConfiguration();
            setConfiguration(null);
            setSuccessMessage('Configuration deleted successfully');
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Failed to delete configuration';
            setError(errorMsg);
            throw err;
        } finally {
            setSaving(false);
        }
    };

    const clearError = () => setError(null);
    const clearSuccessMessage = () => setSuccessMessage(null);

    return {
        configuration,
        availableModels,
        loading,
        saving,
        error,
        successMessage,
        saveConfiguration,
        deleteConfiguration,
        refreshConfiguration: fetchConfiguration,
        refreshAvailableModels: fetchAvailableModels,
        clearError,
        clearSuccessMessage
    };
};
